
import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  image?: string;
  articleAuthor?: string;
  publishedTime?: string;
  contentType?: 'website' | 'article' | 'product' | 'course' | 'news';
  price?: number;
  currency?: string;
  availability?: string;
  locale?: string;
  alternateLocales?: string[];
  /** hreflang alternates for this page, e.g. [{ hreflang: "ru", href: "https://…/?lang=ru" }] */
  hreflangAlternates?: { hreflang: string; href: string }[];
  noindex?: boolean;
}

export const SEOHead = ({ 
  title, 
  description, 
  path, 
  keywords, 
  image,
  articleAuthor,
  publishedTime,
  contentType = 'website',
  price,
  currency = 'AMD',
  availability = 'https://schema.org/InStock',
  locale = 'hy_AM',
  alternateLocales = [],
  hreflangAlternates,
  noindex = false
}: SEOHeadProps) => {
  const hreflangKey = (hreflangAlternates ?? []).map((a) => `${a.hreflang}=${a.href}`).join('|');
  // Meta description: trim to ~155 chars to avoid SERP truncation.
  const clampDesc = (text: string, max = 155) => {
    const t = text.replace(/\s+/g, ' ').trim();
    if (t.length <= max) return t;
    const cut = t.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 80 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:—-]+$/, '') + '…';
  };
  const getEnhancedDescription = () => clampDesc(description);

  // Enhanced keywords with local SEO
  const getEnhancedKeywords = () => {
    const baseKeywords = keywords || '';
    const localKeywords = 'Երևան, Հայաստան, IT ուսուցում, ծրագրավորման դպրոց, տեխնոլոգիական կրթություն';
    const commonKeywords = 'Global IT Academy, ծրագրավորում, programming, web development, դասընթացներ, կարիերա';
    
    return `${baseKeywords}, ${localKeywords}, ${commonKeywords}`.replace(/^,\s*/, '');
  };

  // Helper to ensure absolute URLs for images
  const ensureAbsoluteUrl = (url: string | undefined): string => {
    if (!url) {
      return getDefaultImage();
    }
    
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    const absoluteUrl = `https://globalitacademy.am${url.startsWith('/') ? '' : '/'}${url}`;
    return absoluteUrl;
  };

  // Page-specific default images based on course or page content
  const getDefaultImage = () => {
    // For course pages, try to determine based on course content or use general programming image
    if (path.includes('/course/')) {
      return 'https://globalitacademy.am/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png';
    }
    if (path.includes('/cybersecurity')) {
      return 'https://globalitacademy.am/lovable-uploads/1d875431-108a-485d-a4e3-e27286786183.png';
    }
    if (path.includes('/backend')) {
      return 'https://globalitacademy.am/lovable-uploads/9de645e8-94d4-4ee0-a8d6-1114b3ea741b.png';
    }
    if (path.includes('/frontend') || path.includes('/web-programming')) {
      return 'https://globalitacademy.am/lovable-uploads/3aef1e6b-6e35-4249-905f-4d58c0135648.png';
    }
    if (path.includes('/programming-languages')) {
      return 'https://globalitacademy.am/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png';
    }
    if (path.includes('/news')) {
      return 'https://globalitacademy.am/lovable-uploads/e1b90743-1060-4e0b-acf6-106bbabade35.png';
    }
    // Use main academy image for homepage
    if (path === '/') {
      return 'https://globalitacademy.am/lovable-uploads/1d875431-108a-485d-a4e3-e27286786183.png';
    }
    // Fallback to programming languages image for other pages
    return 'https://globalitacademy.am/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png';
  };
  
  const shareImage = ensureAbsoluteUrl(image);
  const fullUrl = `https://globalitacademy.am${path}`;
  const enhancedDescription = getEnhancedDescription();
  const enhancedKeywords = getEnhancedKeywords();

  // Helper function to create or update meta tag
  const createOrUpdateMeta = (selector: string, attribute: string, content: string, isProperty = false) => {
    let element = document.querySelector(selector);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(isProperty ? 'property' : 'name', selector.replace(/meta\[(name|property)="([^"]+)"\]/, '$2'));
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  };

  // Helper function to create or update link tag
  const createOrUpdateLink = (selector: string, attribute: string, href: string) => {
    let element = document.querySelector(selector);
    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', selector.replace(/link\[rel="([^"]+)"\]/, '$1'));
      document.head.appendChild(element);
    }
    element.setAttribute('href', href);
  };

  useEffect(() => {
    // Compose a concise document title; avoid double-suffixing brand when title already contains it.
    const BRAND = 'Global IT Academy';
    const trimmedTitle = title.trim();
    const includesBrand = trimmedTitle.toLowerCase().includes('global it academy');
    let fullTitle = includesBrand ? trimmedTitle : `${trimmedTitle} | ${BRAND}`;
    if (fullTitle.length > 60) {
      const cut = fullTitle.slice(0, 60);
      const boundary = cut.lastIndexOf(' ');
      fullTitle = `${boundary > 35 ? cut.slice(0, boundary) : cut}…`;
    }
    document.title = fullTitle;

    // Update or create meta description
    createOrUpdateMeta('meta[name="description"]', 'content', enhancedDescription);

    // Update or create keywords
    createOrUpdateMeta('meta[name="keywords"]', 'content', enhancedKeywords);

    // Language and locale meta tags
    createOrUpdateMeta('meta[name="language"]', 'content', locale);
    createOrUpdateMeta('meta[http-equiv="content-language"]', 'content', locale.replace('_', '-'));

    // Geographic meta tags for local SEO
    createOrUpdateMeta('meta[name="geo.region"]', 'content', 'AM-ER');
    createOrUpdateMeta('meta[name="geo.placename"]', 'content', 'Երևան');
    createOrUpdateMeta('meta[name="geo.position"]', 'content', '40.1776;44.5126');
    createOrUpdateMeta('meta[name="ICBM"]', 'content', '40.1776, 44.5126');

    // Additional SEO meta tags
    const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    createOrUpdateMeta('meta[name="robots"]', 'content', robotsContent);
    createOrUpdateMeta('meta[name="googlebot"]', 'content', robotsContent);
    createOrUpdateMeta('meta[name="bingbot"]', 'content', robotsContent);

    // Update or create canonical URL
    createOrUpdateLink('link[rel="canonical"]', 'href', fullUrl);

    // Preload share image for faster social media scraping
    const preloadLink = document.querySelector('link[rel="preload"][as="image"]');
    if (preloadLink) {
      preloadLink.setAttribute('href', shareImage);
    } else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('as', 'image');
      link.setAttribute('href', shareImage);
      document.head.appendChild(link);
    }

    // Update or create Open Graph tags
    createOrUpdateMeta('meta[property="og:title"]', 'content', fullTitle, true);
    createOrUpdateMeta('meta[property="og:description"]', 'content', enhancedDescription, true);
    createOrUpdateMeta('meta[property="og:url"]', 'content', fullUrl, true);
    createOrUpdateMeta('meta[property="og:image"]', 'content', shareImage, true);
    createOrUpdateMeta('meta[property="og:type"]', 'content', contentType, true);
    createOrUpdateMeta('meta[property="og:site_name"]', 'content', 'Global IT Academy', true);
    createOrUpdateMeta('meta[property="og:locale"]', 'content', locale, true);
    createOrUpdateMeta('meta[property="og:image:secure_url"]', 'content', shareImage, true);
    createOrUpdateMeta('meta[property="og:image:type"]', 'content', 'image/png', true);
    createOrUpdateMeta('meta[property="og:image:width"]', 'content', '1200', true);
    createOrUpdateMeta('meta[property="og:image:height"]', 'content', '630', true);
    createOrUpdateMeta('meta[property="og:image:alt"]', 'content', fullTitle, true);

    // Additional Open Graph tags for courses
    if (contentType === 'course' && price) {
      createOrUpdateMeta('meta[property="product:price:amount"]', 'content', price.toString(), true);
      createOrUpdateMeta('meta[property="product:price:currency"]', 'content', currency, true);
      createOrUpdateMeta('meta[property="product:availability"]', 'content', availability, true);
    }

    // Alternate locales
    alternateLocales.forEach(altLocale => {
      createOrUpdateMeta(`meta[property="og:locale:alternate"]`, 'content', altLocale, true);
    });

    // hreflang alternates: always reset so links from a previous route never leak into this one
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    (hreflangAlternates ?? []).forEach(({ hreflang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      document.head.appendChild(link);
    });

    // Update or create Twitter tags
    createOrUpdateMeta('meta[name="twitter:title"]', 'content', fullTitle);
    createOrUpdateMeta('meta[name="twitter:description"]', 'content', enhancedDescription);
    createOrUpdateMeta('meta[name="twitter:url"]', 'content', fullUrl);
    createOrUpdateMeta('meta[name="twitter:image"]', 'content', shareImage);
    createOrUpdateMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    createOrUpdateMeta('meta[name="twitter:site"]', 'content', '@globalitacademy');
    createOrUpdateMeta('meta[name="twitter:creator"]', 'content', '@globalitacademy');

    // Telegram-specific meta tags
    createOrUpdateMeta('meta[property="telegram:image"]', 'content', shareImage, true);
    createOrUpdateMeta('meta[property="telegram:title"]', 'content', fullTitle, true);
    createOrUpdateMeta('meta[property="telegram:description"]', 'content', enhancedDescription, true);

    // Update article-specific meta tags if provided
    if (contentType === 'article' && articleAuthor) {
      let articleAuthorMeta = document.querySelector('meta[property="article:author"]');
      if (!articleAuthorMeta) {
        articleAuthorMeta = document.createElement('meta');
        articleAuthorMeta.setAttribute('property', 'article:author');
        document.head.appendChild(articleAuthorMeta);
      }
      articleAuthorMeta.setAttribute('content', articleAuthor);
    }

    if (contentType === 'article' && publishedTime) {
      let articlePublishedMeta = document.querySelector('meta[property="article:published_time"]');
      if (!articlePublishedMeta) {
        articlePublishedMeta = document.createElement('meta');
        articlePublishedMeta.setAttribute('property', 'article:published_time');
        document.head.appendChild(articlePublishedMeta);
      }
      articlePublishedMeta.setAttribute('content', publishedTime);
    }
  }, [title, description, path, keywords, shareImage, fullUrl, contentType, articleAuthor, publishedTime, enhancedDescription, enhancedKeywords, locale, alternateLocales, hreflangKey, price, currency, availability, noindex]);

  return null;
};
